<template>
  <div>
    <DetailBanner
      v-if="
        dataStore.s1_offerings_in_quiet_period &&
        purchaseFlowStore.selected_offering?.type === 'S1' &&
        !is_review
      "
      variant="secondary"
      class="mt-5 lg:mt-6"
    >
      <template #body>
        <AsteriskNotice text="Registered Offering Quiet Period" />
        <p class="font-inter font-medium text-strongest">
          Submit your investment today. Your payment will be processed upon submission — you'll
          return to sign and finalize once the quiet period ends.
        </p>
      </template>
    </DetailBanner>
  </div>
  <div class="mt-5 md:mt-6">
    <!-- Review Mode: Minimal Summary -->
    <div v-if="is_review" class="py-5 px-4">
      <h3 class="text-lg font-semibold font-inter mb-4 text-strongest">Funding Method</h3>
      <div class="bg-bg-canvas rounded-xl px-4 py-4 flex items-center gap-4 mb-4">
        <img :src="selectedOption?.icon" :alt="selectedOption?.label" class="h-[48px] w-[48px]" />
        <div>
          <p class="font-inter font-semibold text-strongest text-lg">
            {{ selectedOption?.label }}
          </p>
          <p v-if="selectedBankLabel" class="font-inter text-strong text-sm">
            {{ selectedBankLabel }}
          </p>
        </div>
      </div>
      <div v-if="purchaseFlowStore.current_investment.payment_method === 'Check'">
        <div class="flex flex-col lg:flex-row w-full gap-3">
          <div
            class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center font-medium w-full"
          >
            <div>
              <p class="font-inter text-strong">Make check payable to</p>
              <p class="font-inter text-strongest text-lg">Phoenix Energy One, LLC</p>
            </div>
          </div>
          <div class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center w-full">
            <div>
              <p class="font-inter text-strong">
                Send the check through FedEx, UPS, or regular mail to:
              </p>
              <p class="font-inter text-strongest text-lg">
                Phoenix Energy, 18575 Jamboree Rd, Suite 830 Irvine, CA 92612
              </p>
            </div>
          </div>
        </div>
      </div>
      <div v-if="purchaseFlowStore.current_investment.payment_method === 'Card'">
        <div class="bg-bg-canvas rounded-xl px-3 py-4">
          <p class="font-inter text-strong text-sm">
            Your payment was authorized. You will be charged immediately upon signing your
            investment documents.
          </p>
        </div>
      </div>
      <!-- Existing Investment (rollover) -->
      <div v-if="purchaseFlowStore.current_investment.payment_method === 'Existing Investment'">
        <div class="flex flex-col gap-3">
          <div
            class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center font-medium"
          >
            <div>
              <p class="font-inter text-strong">
                You are funding with the proceeds from an existing investment. We will apply the
                proceeds from your selected investment to this purchase once the sale of that
                investment is complete.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="purchaseFlowStore.current_investment.payment_method === 'Wire'"
        class="flex flex-col gap-3"
      >
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center gap-3">
            <div>
              <p class="font-inter text-strong">Bank Name</p>
              <p class="font-inter text-strongest text-lg">Amarillo National Bank</p>
            </div>
            <CopyButton text="Amarillo National Bank" />
          </div>
          <div class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center gap-3">
            <div>
              <p class="font-inter text-strong">ABA Number</p>
              <p class="font-inter text-strongest text-lg">111300958</p>
            </div>
            <CopyButton text="111300958" />
          </div>
        </div>
        <div class="flex flex-col gap-3">
          <div class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center gap-3">
            <div>
              <p class="font-inter text-strong">Reference</p>
              <p class="font-inter text-strongest text-lg">
                Phoenix Energy One, LLC, Suite 830, 18575 Jamboree Rd, Irvine, CA 92612
              </p>
            </div>
            <CopyButton text="Phoenix Energy One, LLC, Suite 830, 18575 Jamboree Rd, Irvine, CA 92612" />
          </div>
          <div class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center gap-3">
            <div>
              <p class="font-inter text-strong">Account Number</p>
              <p class="font-inter text-strongest text-lg">319279</p>
            </div>
            <CopyButton text="319279" />
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Mode -->
    <div v-else class="text-strongest py-5">
      <div class="px-4">
        <div class="mb-6 lg:mb-8">
          <h3 class="text-base lg:text-lg font-semibold font-inter">
            How would you like to fund this investment?
          </h3>
          <p class="font-inter text-strong text-sm">
            Select your preferred method to add funds. Use Checking or Savings Accounts
          </p>
        </div>

        <div class="flex gap-4 items-start">
          <!-- Vertical funding method list -->
          <div
            class="fd-methods flex flex-col gap-2"
            :style="{ width: !isMobile && isExpanded ? '381px' : '100%' }"
          >
            <div
              v-for="option in bankingOptions"
              :key="option.value"
              class="rounded-xl overflow-hidden"
              :class="[
                isOptionSelected(option.value)
                  ? 'bg-container-bg border-2 border-radio-checkbox-selector'
                  : isExpanded
                    ? 'bg-bg-canvas border border-filter-outline'
                    : 'bg-container-bg border border-transparent',
                isOptionDisabled(option.value) ? 'opacity-50' : '',
              ]"
            >
              <!-- Row button -->
              <TooltipProvider :delay-duration="100">
                <Tooltip
                  v-model:open="isCardTooltipOpen"
                  :disabled="!isCardTooltipEligible(option)"
                  disable-closing-trigger
                >
                  <TooltipTrigger
                    as="div"
                    role="button"
                    tabindex="0"
                    class="fd-method-head flex w-full items-center gap-3 px-3 py-2 bg-transparent text-left"
                    :class="isOptionDisabled(option.value) ? 'cursor-not-allowed' : 'cursor-pointer'"
                    @click="handleRowClick(option)"
                    @keydown.enter="handleRowClick(option)"
                    @keydown.space.prevent="handleRowClick(option)"
                  >
                    <Icon
                      v-if="isOptionDisabled(option.value)"
                      name="CircleSlash2"
                      class="h-4 w-4 shrink-0 text-light"
                    />
                    <div
                      v-else
                      class="h-4 w-4 rounded-full flex items-center justify-center shrink-0 border border-radio-checkbox-selector"
                      :class="
                        isOptionSelected(option.value)
                          ? 'bg-radio-checkbox-checked border-transparent'
                          : ''
                      "
                    >
                      <Icon
                        v-if="isOptionSelected(option.value)"
                        name="Check"
                        class="text-input-bg h-3 w-3"
                        :stroke-width="2.5"
                      />
                    </div>
                    <div class="flex-1 font-inter">
                      <p :class="isOptionSelected(option.value) ? 'font-semibold' : 'font-medium'">
                        {{ option.label }}<span v-if="isOptionDisabled(option.value)">
                          (unavailable)</span
                        >
                      </p>
                      <p
                        v-if="isCardTooltipEligible(option)"
                        class="text-xs font-normal text-light mt-0.5"
                      >
                        {{ cardIneligibilityReason }}
                      </p>
                    </div>
                    <img
                      :src="option.icon"
                      :alt="option.label"
                      class="fd-method-icon object-cover shrink-0"
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    v-if="isCardTooltipEligible(option)"
                    side="top"
                    :side-offset="8"
                    :collision-padding="16"
                    class="!bg-bg-canvas !text-strongest w-72 rounded-2xl ring-1 ring-outline-dividers p-4 text-left shadow-lg sm:w-80"
                  >
                    <div class="flex items-center gap-2 mb-2">
                      <Icon name="CircleQuestionMark" class="h-3.5 w-3.5 stroke-info" />
                      <span class="font-inter font-semibold text-sm text-info">Card &amp; Wallets</span>
                    </div>
                    <p class="font-inter text-sm font-light leading-relaxed text-strong">
                      {{ cardIneligibilityReason }}
                    </p>
                    <div class="my-3 h-px bg-outline-dividers" />
                    <div class="flex items-center justify-between gap-3">
                      <span class="font-inter text-sm text-strongest"
                        >Call us if you have any queries</span
                      >
                      <Button
                        variant="paper-primary"
                        size="xs"
                        class="shrink-0"
                        @click.stop="callCardSupport"
                      >
                        <template #icon><Icon name="Phone" class="h-4 w-4" /></template>
                        <template #label>Call 855-732-6395</template>
                      </Button>
                    </div>
                    <TooltipArrow class="fill-bg-canvas" />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <!-- Mobile: detail expands inline below the selected row -->
              <Transition
                :css="false"
                @enter="accEnter"
                @after-enter="accAfterEnter"
                @leave="accLeave"
                @after-leave="accAfterLeave"
              >
                <div
                  v-if="isMobile && isOptionSelected(option.value)"
                  class="px-3 pb-4 pt-3 border-t border-filter-outline"
                >
                  <h3 class="text-sm font-semibold font-inter mb-3">
                    {{ fundingTitleForOption(option.value) }}
                  </h3>
                  <!-- Bank Accounts (with accounts) -->
                  <div v-if="option.value === 'ACH' && dataStore.bank_accounts?.length > 0">
                    <div class="grid grid-cols-1 gap-3 w-full">
                      <div
                        v-for="bank in dataStore.bank_accounts"
                        :key="bank.record_id"
                        @click="
                          purchaseFlowStore.current_investment.funding_account = bank.record_id
                        "
                        class="cursor-pointer rounded-xl p-3 flex items-center justify-between gap-3"
                        :class="
                          isBankSelected(bank.record_id)
                            ? 'bg-radio-checkbox-selector border-2 border-radio-checkbox-selector'
                            : ''
                        "
                      >
                        <div class="flex items-center gap-3">
                          <div
                            class="h-5 w-5 rounded-full flex items-center justify-center border border-radio-checkbox-outline shrink-0"
                            :class="
                              isBankSelected(bank.record_id)
                                ? 'bg-radio-checkbox-checked border-transparent'
                                : 'bg-transparent'
                            "
                          >
                            <Icon
                              v-if="isBankSelected(bank.record_id)"
                              name="Check"
                              class="text-container-bg h-3 w-3"
                              :stroke-width="2.5"
                            />
                          </div>
                          <div>
                            <p
                              class="font-inter text-sm"
                              :class="
                                isBankSelected(bank.record_id) ? 'font-semibold' : 'font-medium'
                              "
                            >
                              Account ending in ******{{
                                bank.institution_id == "ins_56" ||
                                bank.institution_id == "ins_13" ||
                                bank.institution_id == "ins_127990"
                                  ? bank.mask
                                  : bank.account_number.slice(-4)
                              }}
                            </p>
                            <p class="text-xs font-medium font-inter text-strong">
                              {{ bank.account_type }} account in
                              {{ bank.financial_institution_name }}
                            </p>
                          </div>
                        </div>
                        <img
                          v-if="bank.logo"
                          class="h-8 w-8 rounded-full shrink-0"
                          :src="'data:image/png;base64, ' + bank.logo + ''"
                          alt="bank logo"
                        />
                        <Icon
                          v-else
                          name="Landmark"
                          class="h-6 w-6 text-strongest shrink-0"
                          :stroke-width="2"
                        />
                      </div>
                    </div>
                    <Button
                      @click="plaidLink.open('Funding')"
                      variant="paper-primary"
                      class="w-auto mt-4"
                      size="md-xs"
                    >
                      <template #label>Connect a new bank account</template>
                    </Button>
                  </div>
                  <!-- Bank Accounts (no accounts) -->
                  <div
                    v-else-if="option.value === 'ACH' && dataStore.bank_accounts?.length === 0"
                    class="flex flex-col items-center justify-center pb-2"
                  >
                    <Icon name="Landmark" class="h-6 w-6 text-tab-icon-selected mb-2" />
                    <p class="font-medium font-inter text-strong text-sm">
                      No connected bank accounts
                    </p>
                    <Button
                      @click="plaidLink.open('Funding')"
                      variant="primary-grey"
                      class="w-auto mt-3"
                      size="md-xs"
                    >
                      <template #label>Connect a new bank account</template>
                    </Button>
                  </div>
                  <!-- Wire -->
                  <div v-if="option.value === 'Wire'" class="flex flex-col gap-2">
                    <div
                      class="bg-bg-canvas rounded-xl px-3 py-2 flex justify-between items-center gap-3"
                    >
                      <div>
                        <p class="font-inter text-strong text-sm">Reference</p>
                        <p class="font-inter text-strongest">
                          Phoenix Energy One, LLC, Suite 830, 18575 Jamboree Rd, Irvine, CA 92612
                        </p>
                      </div>
                      <CopyButton text="Phoenix Energy One, LLC, Suite 830, 18575 Jamboree Rd, Irvine, CA 92612" />
                    </div>
                    <div
                      class="bg-bg-canvas rounded-xl px-3 py-2 flex justify-between items-center gap-3"
                    >
                      <div>
                        <p class="font-inter text-strong text-sm">Bank Name</p>
                        <p class="font-inter text-strongest">Amarillo National Bank</p>
                      </div>
                      <CopyButton text="Amarillo National Bank" />
                    </div>
                    <div
                      class="bg-bg-canvas rounded-xl px-3 py-2 flex justify-between items-center gap-3"
                    >
                      <div>
                        <p class="font-inter text-strong text-sm">ABA Number</p>
                        <p class="font-inter text-strongest">111300958</p>
                      </div>
                      <CopyButton text="111300958" />
                    </div>
                    <div
                      class="bg-bg-canvas rounded-xl px-3 py-2 flex justify-between items-center gap-3"
                    >
                      <div>
                        <p class="font-inter text-strong text-sm">Account Number</p>
                        <p class="font-inter text-strongest">319279</p>
                      </div>
                      <CopyButton text="319279" />
                    </div>
                  </div>
                  <!-- Check -->
                  <div v-if="option.value === 'Check'" class="flex flex-col gap-2">
                    <div
                      class="bg-bg-canvas rounded-xl px-3 py-2 flex justify-between items-center"
                    >
                      <div>
                        <p class="font-inter text-strong text-sm">Make check payable to</p>
                        <p class="font-inter text-strongest">Phoenix Energy One, LLC</p>
                      </div>
                    </div>
                    <div
                      class="bg-bg-canvas rounded-xl px-3 py-2 flex justify-between items-center"
                    >
                      <div>
                        <p class="font-inter text-strong text-sm">
                          Send the check through FedEx, UPS, or regular mail to:
                        </p>
                        <p class="font-inter text-strongest">
                          Phoenix Energy, 18575 Jamboree Rd, Suite 830 Irvine, CA 92612
                        </p>
                      </div>
                    </div>
                  </div>
                  <!-- Existing Investment -->
                  <div v-if="option.value === 'Existing Investment'">
                    <div class="bg-bg-canvas rounded-xl px-3 py-3">
                      <p class="font-inter text-strong text-sm">
                        You are funding with the proceeds from an existing investment. We will apply
                        the proceeds from your selected investment to this purchase once the sale of
                        that investment is complete.
                      </p>
                    </div>
                  </div>
                  <!-- Card: already authorized -->
                  <div
                    v-if="cardAlreadyAuthorized && option.value === 'Card'"
                    class="flex flex-col gap-3"
                  >
                    <div class="bg-bg-canvas rounded-xl px-3 py-3 flex items-center gap-3">
                      <div
                        class="h-8 w-8 rounded-full bg-radio-checkbox-checked flex items-center justify-center shrink-0"
                      >
                        <Icon name="Check" class="text-container-bg h-4 w-4" :stroke-width="2.5" />
                      </div>
                      <div>
                        <p class="font-inter font-semibold text-strongest text-sm">
                          Payment method registered
                        </p>
                        <p class="font-inter text-strong text-xs">
                          Your payment has been authorized. You will be charged immediately upon
                          signing your investment documents.
                        </p>
                      </div>
                    </div>
                    <Button @click="resetCard" variant="paper-primary" class="w-auto" size="md-xs">
                      <template #label>Use a different payment method</template>
                    </Button>
                  </div>
                  <!-- Card: enter details -->
                  <div v-else-if="option.value === 'Card'" class="flex flex-col gap-3">
                    <div
                      v-if="stripeCheckout.error.value"
                      class="rounded-xl bg-red-50 px-4 py-4 flex items-center gap-3"
                    >
                      <Icon
                        name="AlertCircle"
                        class="h-5 w-5 text-error shrink-0 mt-0.5"
                        :stroke-width="2"
                      />
                      <div class="flex-1">
                        <p class="text-sm font-semibold text-error">Card payment unavailable</p>
                        <p class="text-sm text-error mt-0.5">
                          We couldn't load the card payment form. Please select a different payment
                          method, or try again.
                        </p>
                        <!-- <button
                            class="mt-2 text-sm font-semibold text-red-700 underline underline-offset-2 cursor-pointer bg-transparent border-none p-0"
                            @click="retryCard"
                          >
                            Try again
                          </button> -->
                      </div>
                    </div>
                    <div v-if="stripeCheckout.loading.value" class="flex items-center gap-2 py-3">
                      <Icon name="Loader" class="h-4 w-4 animate-spin text-strong" />
                      <p class="text-sm font-medium text-strong">Loading payment form…</p>
                    </div>
                    <div
                      v-if="!stripeCheckout.error.value"
                      ref="mobileStripeRef"
                      class="min-h-[200px]"
                    ></div>
                    <p class="font-inter text-light text-xs leading-relaxed">
                      Pay by card, Apple Pay, Google Pay, or Cash App. A temporary hold will be
                      placed on your selected payment method for the amount you indicated you want
                      to invest, and that hold will remain in place until you complete your
                      investment. Contact (855) 732-6395 or InvestorRelations@PhoenixEnergy.com to
                      let us know if you no longer wish to invest. No amount will be actually
                      charged until you sign your investment documents. Phoenix Energy covers all
                      card processing fees — you receive the full value of your investment with no
                      fees deducted from your principal.
                    </p>
                  </div>
                </div>
              </Transition>
            </div>
          </div>

          <!-- Desktop: panel slides in beside the method column -->
          <Transition name="fd-panel">
            <div
              v-if="!isMobile && showPanel"
              class="flex-1 min-w-0 bg-container-bg rounded-xl p-5 font-medium border-2 border-radio-checkbox-selector"
            >
              <Transition name="fd-fade" mode="out-in">
                <div
                  :key="purchaseFlowStore.current_investment.payment_method"
                  class="flex flex-col gap-4"
                >
                  <h3 class="text-base lg:text-lg font-semibold font-inter">
                    {{ fundingTitleForOption(purchaseFlowStore.current_investment.payment_method) }}
                  </h3>
                  <!-- Bank Accounts (with accounts) -->
                  <div
                    v-if="
                      purchaseFlowStore.current_investment.payment_method === 'ACH' &&
                      dataStore.bank_accounts?.length > 0
                    "
                  >
                    <div class="grid grid-cols-1 gap-3 w-full">
                      <div
                        v-for="bank in dataStore.bank_accounts"
                        :key="bank.record_id"
                        @click="
                          purchaseFlowStore.current_investment.funding_account = bank.record_id
                        "
                        class="cursor-pointer rounded-xl p-4 flex items-center justify-between gap-3"
                        :class="
                          isBankSelected(bank.record_id)
                            ? 'bg-radio-checkbox-selector border-2 border-radio-checkbox-selector'
                            : ''
                        "
                      >
                        <div class="flex items-center gap-5">
                          <div
                            class="h-6 w-6 rounded-full flex items-center justify-center border border-radio-checkbox-outline shrink-0"
                            :class="
                              isBankSelected(bank.record_id)
                                ? 'bg-radio-checkbox-checked border-transparent'
                                : 'bg-transparent'
                            "
                          >
                            <Icon
                              v-if="isBankSelected(bank.record_id)"
                              name="Check"
                              class="text-container-bg h-4.5 w-4.5"
                              :stroke-width="2.5"
                            />
                          </div>
                          <div>
                            <p
                              class="font-inter text-sm lg:text-base"
                              :class="
                                isBankSelected(bank.record_id) ? 'font-semibold' : 'font-medium'
                              "
                            >
                              Account ending in ******{{
                                bank.institution_id == "ins_56" ||
                                bank.institution_id == "ins_13" ||
                                bank.institution_id == "ins_127990"
                                  ? bank.mask
                                  : bank.account_number.slice(-4)
                              }}
                            </p>
                            <p class="text-sm font-medium font-inter text-strong">
                              {{ bank.account_type }} account in
                              {{ bank.financial_institution_name }}
                            </p>
                          </div>
                        </div>
                        <img
                          v-if="bank.logo"
                          class="h-12 w-12 rounded-full shrink-0"
                          :src="'data:image/png;base64, ' + bank.logo + ''"
                          alt="bank logo"
                        />
                        <Icon
                          v-else
                          name="Landmark"
                          class="h-10 w-10 text-strongest shrink-0"
                          :stroke-width="2"
                        />
                      </div>
                    </div>
                    <Button
                      @click="plaidLink.open('Funding')"
                      variant="paper-primary"
                      class="w-auto mt-5"
                      size="md-xs"
                    >
                      <template #label>Connect a new bank account</template>
                    </Button>
                  </div>
                  <!-- Bank Accounts (no accounts) -->
                  <div
                    v-else-if="
                      purchaseFlowStore.current_investment.payment_method === 'ACH' &&
                      dataStore.bank_accounts?.length === 0
                    "
                    class="flex flex-col items-center justify-center pb-4"
                  >
                    <Icon name="Landmark" class="h-6 w-6 text-tab-icon-selected mb-3" />
                    <p class="font-medium font-inter text-strong">No connected bank accounts</p>
                    <Button
                      @click="plaidLink.open('Funding')"
                      variant="primary-grey"
                      class="w-auto mt-[1.313rem]"
                      size="md-xs"
                    >
                      <template #label>Connect a new bank account</template>
                    </Button>
                  </div>
                  <!-- Wire -->
                  <div
                    v-if="purchaseFlowStore.current_investment.payment_method === 'Wire'"
                    class="flex flex-col gap-2"
                  >
                    <div
                      class="bg-bg-canvas rounded-xl px-3 py-2.5 flex justify-between items-center gap-3"
                    >
                      <div>
                        <p class="font-inter text-strong text-sm">Reference</p>
                        <p class="font-inter text-strongest">
                          Phoenix Energy One, LLC, Suite 830, 18575 Jamboree Rd, Irvine, CA 92612
                        </p>
                      </div>
                      <CopyButton text="Phoenix Energy One, LLC, Suite 830, 18575 Jamboree Rd, Irvine, CA 92612" />
                    </div>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
                      <div
                        class="bg-bg-canvas rounded-xl px-3 py-2.5 flex justify-between items-center gap-3"
                      >
                        <div>
                          <p class="font-inter text-strong text-sm">Bank Name</p>
                          <p class="font-inter text-strongest">Amarillo National Bank</p>
                        </div>
                        <CopyButton text="Amarillo National Bank" />
                      </div>
                      <div
                        class="bg-bg-canvas rounded-xl px-3 py-2.5 flex justify-between items-center gap-3"
                      >
                        <div>
                          <p class="font-inter text-strong text-sm">ABA Number</p>
                          <p class="font-inter text-strongest">111300958</p>
                        </div>
                        <CopyButton text="111300958" />
                      </div>
                    </div>
                    <div
                      class="bg-bg-canvas rounded-xl px-3 py-2.5 flex justify-between items-center gap-3"
                    >
                      <div>
                        <p class="font-inter text-strong text-sm">Account Number</p>
                        <p class="font-inter text-strongest">319279</p>
                      </div>
                      <CopyButton text="319279" />
                    </div>
                  </div>
                  <!-- Check -->
                  <div v-if="purchaseFlowStore.current_investment.payment_method === 'Check'">
                    <div class="flex flex-col gap-2">
                      <div
                        class="bg-bg-canvas rounded-xl px-3 py-2.5 flex justify-between items-center"
                      >
                        <div>
                          <p class="font-inter text-strong text-sm">Make check payable to</p>
                          <p class="font-inter text-strongest">Phoenix Energy One, LLC</p>
                        </div>
                      </div>
                      <div
                        class="bg-bg-canvas rounded-xl px-3 py-2.5 flex justify-between items-center"
                      >
                        <div>
                          <p class="font-inter text-strong text-sm">
                            Send the check through FedEx, UPS, or regular mail to:
                          </p>
                          <p class="font-inter text-strongest">
                            Phoenix Energy, 18575 Jamboree Rd, Suite 830 Irvine, CA 92612
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Existing Investment (rollover) -->
                  <div
                    v-if="
                      purchaseFlowStore.current_investment.payment_method === 'Existing Investment'
                    "
                  >
                    <div class="flex flex-col gap-3">
                      <div
                        class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center font-medium"
                      >
                        <div>
                          <p class="font-inter text-strong">
                            You are funding with the proceeds from an existing investment. We will
                            apply the proceeds from your selected investment to this purchase once
                            the sale of that investment is complete.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Card: already authorized -->
                  <div v-if="cardAlreadyAuthorized" class="flex flex-col gap-4">
                    <div class="bg-bg-canvas rounded-xl px-4 py-4 flex items-center gap-4">
                      <div
                        class="h-10 w-10 rounded-full bg-radio-checkbox-checked flex items-center justify-center shrink-0"
                      >
                        <Icon name="Check" class="text-container-bg h-5 w-5" :stroke-width="2.5" />
                      </div>
                      <div>
                        <p class="font-inter font-semibold text-strongest">
                          Payment method registered
                        </p>
                        <p class="font-inter text-strong text-sm">
                          Your payment has been authorized. You will be charged immediately upon
                          signing your investment documents.
                        </p>
                      </div>
                    </div>
                    <Button @click="resetCard" variant="paper-primary" class="w-auto" size="md-xs">
                      <template #label>Use a different payment method</template>
                    </Button>
                  </div>
                  <!-- Card: enter details -->
                  <div
                    v-else-if="purchaseFlowStore.current_investment.payment_method === 'Card'"
                    class="flex flex-col gap-4"
                  >
                    <div
                      v-if="stripeCheckout.error.value"
                      class="rounded-xl bg-red-50 px-4 py-4 flex items-center gap-3"
                    >
                      <Icon
                        name="AlertCircle"
                        class="h-5 w-5 text-error shrink-0 mt-0.5"
                        :stroke-width="2"
                      />
                      <div class="flex-1">
                        <p class="text-sm font-semibold text-error">Card payment unavailable</p>
                        <p class="text-sm text-error mt-0.5">
                          We couldn't load the card payment form. Please select a different payment
                          method, or try again.
                        </p>
                        <!-- <button
                          class="mt-2 text-sm font-semibold text-red-700 underline underline-offset-2 cursor-pointer bg-transparent border-none p-0"
                          @click="retryCard"
                        >
                          Try again
                        </button> -->
                      </div>
                    </div>
                    <div v-if="stripeCheckout.loading.value" class="flex items-center gap-2 py-4">
                      <Icon name="Loader" class="h-5 w-5 animate-spin text-strong" />
                      <p class="text-sm font-medium text-strong">Loading payment form…</p>
                    </div>
                    <div
                      v-if="!stripeCheckout.error.value"
                      ref="desktopStripeRef"
                      class="min-h-[200px]"
                    ></div>
                    <p class="font-inter text-light text-xs leading-relaxed">
                      Pay by card, Apple Pay, Google Pay, or Cash App. A temporary hold will be
                      placed on your selected payment method for the amount you indicated you want
                      to invest, and that hold will remain in place until you complete your
                      investment. Contact (855) 732-6395 or InvestorRelations@PhoenixEnergy.com to
                      let us know if you no longer wish to invest. No amount will be actually
                      charged until you sign your investment documents. Phoenix Energy covers all
                      card processing fees — you receive the full value of your investment with no
                      fees deducted from your principal.
                    </p>
                  </div>
                </div>
              </Transition>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, onMounted, onBeforeUnmount, nextTick, watch, ref } from "vue";
import { useDataStore, usePurchaseFlowStore, useUIStore } from "@/stores";
import { Button, Icon, DetailBanner, AsteriskNotice, CopyButton } from "@/components";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import wireImg from "@/assets/images/wire.png";
import checkImg from "@/assets/images/check.png";
import bankImg from "@/assets/images/bank.png";
import cardImg from "@/assets/images/card.png";

// --- Card payment eligibility rules (update here when requirements change) ---
const CARD_RULES = {
  allowed_offering_types: ["S1"],
  max_amount: 10000,
};

const props = defineProps({
  plaidLink: { type: Object, required: true },
  stripeCheckout: { type: Object, required: true },
  is_review: { type: Boolean, default: false },
});

const dataStore = useDataStore();
const purchaseFlowStore = usePurchaseFlowStore();
const UIStore = useUIStore();

// ─── Mobile breakpoint ────────────────────────────────────────────────────────
const isMobile = ref(false);
let mq;
function syncBreakpoint() {
  isMobile.value = mq?.matches ?? false;
}

// ─── Desktop panel visibility ─────────────────────────────────────────────────
// Starts hidden so the column can animate to its narrow width before the panel
// slides in. Set to true on mount (or on first selectMethod call).
const showPanel = ref(false);
let panelTimer;

const isExpanded = computed(() => !!purchaseFlowStore.current_investment.payment_method);

function selectMethod(value) {
  purchaseFlowStore.current_investment.payment_method = value;
  if (!isMobile.value && !showPanel.value) {
    clearTimeout(panelTimer);
    panelTimer = setTimeout(() => {
      showPanel.value = true;
    }, 400);
  }
}

onMounted(() => {
  mq = window.matchMedia("(max-width: 768px)");
  syncBreakpoint();
  mq.addEventListener("change", syncBreakpoint);

  // If a method is already defaulted (e.g. ACH from parent onMounted), show
  // the desktop panel after the initial paint so the width animation plays.
  if (!isMobile.value && purchaseFlowStore.current_investment.payment_method) {
    nextTick(() => {
      showPanel.value = true;
    });
  }
});

onBeforeUnmount(() => {
  mq?.removeEventListener("change", syncBreakpoint);
  clearTimeout(panelTimer);
});

// When resizing mobile → desktop with a method already selected, show the panel
watch(isMobile, (mobile) => {
  if (!mobile && purchaseFlowStore.current_investment.payment_method) {
    showPanel.value = true;
  }
});

// ─── Stripe dark-mode sync ────────────────────────────────────────────────────
watch(
  () => UIStore.is_dark_mode,
  () => {
    props.stripeCheckout.updateAppearance();
  }
);

const cardIneligibilityReason = computed(() => {
  const type = purchaseFlowStore.selected_offering?.type;
  const amount = Number(String(purchaseFlowStore.current_investment.amount).replace(/,/g, "")) || 0;

  if (!CARD_RULES.allowed_offering_types.includes(type)) {
    return "Not available for this offering type";
  }
  if (purchaseFlowStore.signedInvestmentStatus.has_signed_investment) {
    return "Only available for your first investment with Phoenix Energy";
  }
  if (amount > CARD_RULES.max_amount) {
    return `Only available for investments of $${CARD_RULES.max_amount.toLocaleString()} or less`;
  }
  return null;
});

const isCardEligible = computed(() => !cardIneligibilityReason.value);

const isCardTooltipOpen = ref(false);
function callCardSupport() {
  window.location.href = "tel:8557326395";
}

function isCardTooltipEligible(option) {
  return (
    option.value === "Card" &&
    !!cardIneligibilityReason.value &&
    !purchaseFlowStore.selected_rollover
  );
}

function handleRowClick(option) {
  if (isOptionDisabled(option.value)) {
    if (isCardTooltipEligible(option)) {
      isCardTooltipOpen.value = !isCardTooltipOpen.value;
    }
    return;
  }
  selectMethod(option.value);
}

function activePiId() {
  const inv = purchaseFlowStore.current_investment;
  return String(inv.funding_account ?? "").startsWith("pi_") ? inv.funding_account : null;
}

watch(
  isCardEligible,
  (eligible) => {
    if (!eligible && purchaseFlowStore.current_investment.payment_method === "Card") {
      props.stripeCheckout.cancelPaymentIntent(activePiId());
      purchaseFlowStore.current_investment.payment_method = "ACH";
      purchaseFlowStore.current_investment.funding_account = null;
    }
  },
  { immediate: true }
);

watch(
  () => purchaseFlowStore.current_investment.payment_method,
  (newMethod, oldMethod) => {
    if (oldMethod === "Card" && newMethod !== "Card") {
      const piId = activePiId();
      if (piId) {
        props.stripeCheckout.cancelPaymentIntent(piId);
        purchaseFlowStore.current_investment.funding_account = null;
      }
      // Always reset Stripe so the element re-initializes cleanly if user returns to Card
      props.stripeCheckout.reset();
    }
  }
);

const bankingOptions = [
  { label: "Bank account", value: "ACH", icon: bankImg },
  { label: "Wire transfer", value: "Wire", icon: wireImg },
  { label: "Check", value: "Check", icon: checkImg },
  { label: "Card & Wallets", value: "Card", icon: cardImg },
];

onBeforeMount(() => {
  if (purchaseFlowStore.selected_rollover) {
    bankingOptions.unshift({
      label: "Existing Investment",
      value: "Existing Investment",
      icon: bankImg,
    });
  } else {
    if (purchaseFlowStore.current_investment.payment_method === "Existing Investment") {
      purchaseFlowStore.current_investment.payment_method = "ACH";
    }
  }
});

const mobileStripeRef = ref(null);
const desktopStripeRef = ref(null);

// mobileStripeRef is inside a v-for, so Vue 3 makes it an array. Unwrap it.
const mobileStripeEl = computed(() => {
  const v = mobileStripeRef.value;
  return Array.isArray(v) ? (v[0] ?? null) : v;
});

async function initializeStripeEl(el) {
  if (purchaseFlowStore.current_investment.payment_method !== "Card") return;
  if (props.stripeCheckout.loading.value) return;

  const contact = dataStore.contact;
  const name = `${contact?.first_name ?? ""} ${contact?.last_name ?? ""}`.trim() || "Investor";
  const email = contact?.email ?? "";
  const amount = purchaseFlowStore.current_investment.amount;

  await props.stripeCheckout.initialize(el, { name, email, amount });
}

// Only reset when BOTH containers are absent. This prevents the desktop panel's
// CSS leave-transition (200ms) from clearing the ref after the mobile element has
// already claimed it (and vice-versa when resizing back to desktop).
function maybeReset() {
  if (mobileStripeEl.value || desktopStripeRef.value) return;
  if (!props.stripeCheckout.error.value) {
    props.stripeCheckout.reset();
  }
}

watch(
  () => purchaseFlowStore.current_investment.payment_method,
  async (method) => {
    if (method !== "Card") return;
    const el = mobileStripeEl.value || desktopStripeRef.value;
    if (!el) return;
    await initializeStripeEl(el);
  }
);

watch(mobileStripeEl, async (el) => {
  if (!el) {
    maybeReset();
    return;
  }
  await initializeStripeEl(el);
});

watch(desktopStripeRef, async (el) => {
  if (!el) {
    maybeReset();
    return;
  }
  await initializeStripeEl(el);
});

const cardAlreadyAuthorized = computed(() => {
  const inv = purchaseFlowStore.current_investment;
  return inv.payment_method === "Card" && !!String(inv.funding_account ?? "").startsWith("pi_");
});

function resetCard() {
  props.stripeCheckout.cancelPaymentIntent(activePiId());
  purchaseFlowStore.current_investment.funding_account = null;
  props.stripeCheckout.reset();
}

async function retryCard() {
  props.stripeCheckout.reset();
  await nextTick();
  const el = mobileStripeRef.value || desktopStripeRef.value;
  if (!el) return;
  const contact = dataStore.contact;
  const name = `${contact?.first_name ?? ""} ${contact?.last_name ?? ""}`.trim() || "Investor";
  const email = contact?.email ?? "";
  const amount = purchaseFlowStore.current_investment.amount;
  await props.stripeCheckout.initialize(el, { name, email, amount });
}

const selectedOption = computed(() =>
  bankingOptions.find((o) => o.value === purchaseFlowStore.current_investment.payment_method)
);
//hi
const selectedBankLabel = computed(() => {
  if (purchaseFlowStore.current_investment.payment_method !== "ACH") return null;
  const bank = dataStore.bank_accounts?.find(
    (b) => b.record_id === purchaseFlowStore.current_investment.funding_account
  );
  if (!bank) return null;
  const lastFour =
    bank.institution_id === "ins_56" ||
    bank.institution_id === "ins_13" ||
    bank.institution_id === "ins_127990"
      ? bank.mask
      : bank.account_number.slice(-4);
  return `${bank.account_type} account ending in ****${lastFour} · ${bank.financial_institution_name}`;
});

const isOptionSelected = (optionValue) => {
  return purchaseFlowStore.current_investment.payment_method === optionValue;
};

const isOptionDisabled = (optionValue) => {
  if (purchaseFlowStore.selected_rollover) return optionValue !== "Existing Investment";
  if (optionValue === "Card") return !isCardEligible.value;
  return false;
};

const isBankSelected = (optionValue) => {
  return purchaseFlowStore.current_investment.funding_account === optionValue;
};

const fundingTitleForOption = (optionValue) => {
  switch (optionValue) {
    case "ACH":
      return "Connected bank accounts";
    case "Wire":
      return "Please wire funds to Phoenix Energy";
    case "Check":
    case "Existing Investment":
      return "Please proceed with the following information";
    case "Card":
      return "Pay by card or digital wallet";
    default:
      return "Select a funding method";
  }
};

// ─── Mobile accordion JS hooks ───────────────────────────────────────────────
function accEnter(el, done) {
  el.style.overflow = "hidden";
  el.style.height = "0";
  el.style.opacity = "0";
  el.offsetHeight; // force reflow so the "from" state is painted first
  el.style.transition = "height 0.32s ease, opacity 0.25s ease";
  el.style.height = el.scrollHeight + "px";
  el.style.opacity = "1";
  setTimeout(done, 320);
}
function accAfterEnter(el) {
  el.style.cssText = "";
}
function accLeave(el, done) {
  el.style.overflow = "hidden";
  el.style.height = el.scrollHeight + "px";
  el.offsetHeight;
  el.style.transition = "height 0.28s ease, opacity 0.22s ease";
  el.style.height = "0";
  el.style.opacity = "0";
  setTimeout(done, 280);
}
function accAfterLeave(el) {
  el.style.cssText = "";
}
</script>

<style scoped>
/* ─── Methods column: animated width collapse ────────── */
.fd-methods {
  transition: width 0.4s ease;
}

/* ─── Method card head: fixed row height ─────────────── */
.fd-method-head {
  min-height: 72px;
  border: none;
}

/* ─── Method icon size ───────────────────────────────── */
.fd-method-icon {
  width: 60px;
  height: 60px;
}

/* ─── Desktop panel slide-in ─────────────────────────── */
.fd-panel-enter-active {
  transition:
    opacity 0.35s ease-out,
    transform 0.35s ease-out;
}
.fd-panel-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.fd-panel-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.fd-panel-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* ─── Content crossfade when switching methods ───────── */
.fd-fade-enter-active,
.fd-fade-leave-active {
  transition: opacity 0.2s ease;
}
.fd-fade-enter-from,
.fd-fade-leave-to {
  opacity: 0;
}

/* ─── Responsive ─────────────────────────────────────── */
@media (max-width: 768px) {
  .fd-method-head {
    min-height: 56px;
  }

  .fd-method-icon {
    width: 40px;
    height: 40px;
  }
}
</style>
